import type { QueryClient } from '@tanstack/react-query';
import {
	getDeclarationsNewOverview,
	type EngineOverview,
	type EnginePeriodDetail,
	type EngineRegime,
} from '@/shared/services/declarationsNew.service';
import { isCheckingReconnection } from '../lib/reconnection';

export const DECLARATIONS_NEW_QUERY_KEYS = {
	all: ['declarationsNew'] as const,
	overview: ['declarationsNew', 'overview'] as const,
	period: (period: string, regime?: EngineRegime) => ['declarationsNew', 'period', period, regime ?? 'principal'] as const,
	annual: (year: number) => ['declarationsNew', 'annual', year] as const,
	payroll: (period: string) => ['declarationsNew', 'payroll', period] as const,
};

const OVERVIEW_POLL_MS = 15_000;

const RECONNECTION_POLL_MS = 30_000;

const hasMonthInFlight = (overview?: EngineOverview) =>
	Boolean(overview?.years.some((year) => year.months.some((month) => (month.cells ?? [month]).some((cell) => cell.state === 'presentando'))));

export const declarationsNewOverviewQuery = {
	queryKey: DECLARATIONS_NEW_QUERY_KEYS.overview,
	queryFn: getDeclarationsNewOverview,
	staleTime: 60_000,
	refetchInterval: (query: { state: { data?: EngineOverview } }) => {
		if (hasMonthInFlight(query.state.data)) return OVERVIEW_POLL_MS;
		return isCheckingReconnection() && query.state.data?.sync === 'reconectar' ? RECONNECTION_POLL_MS : false;
	},
};

export const refreshOverviewIfBehind = (queryClient: QueryClient, detail: EnginePeriodDetail, requestedAt: number) => {
	const cached = queryClient.getQueryState<EngineOverview>(DECLARATIONS_NEW_QUERY_KEYS.overview);
	if (!cached?.data || cached.dataUpdatedAt >= requestedAt) return;
	const cell = cached.data.years
		.flatMap((year) => year.months)
		.find((month) => month.period === detail.period)
		?.cells?.find((candidate) => candidate.regime === detail.regime);
	if (cell && cell.state !== detail.state) void queryClient.invalidateQueries({ queryKey: DECLARATIONS_NEW_QUERY_KEYS.overview });
};
